import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { applyDiscount, reconcileReferralDiscount } from 'actions/purchase';
import { getHasDynamicPricing } from 'utils/purchase/dynamicPricing';
import { isCheckoutDiscountCodeAvailable } from 'utils/purchase/checkoutDiscountEligibility';
import {
  claimReferralCoupon,
  readReferralCouponHandoff,
  settleReferralCoupon,
  skipReferralCoupon,
} from 'utils/purchase/referralCouponHandoff';

// An already claimed request can outlive a route remount. Keep payment hidden until it settles.
const automaticApplications = new Map();

/** Resolve a token-bound referral coupon before exposing any checkout payment control. */
export default function useReferralCheckoutCoupon({
  token,
  loaded,
  purchase,
  features,
  purchaseStep,
}) {
  const dispatch = useDispatch();
  const manualCode = useSelector((state) => state.form?.discountCode?.values?.discountCode);
  const [settledToken, setSettledToken] = useState(null);
  const [inFlightToken, setInFlightToken] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const checkoutStep = ['checkout', 'quickCheckout'].includes(purchaseStep);
  const ready = checkoutStep && loaded && token && purchase.token === token;
  const handoff = useMemo(() => (ready ? readReferralCouponHandoff(token) : null), [ready, token]);
  const unresolved = ['pending', 'attempted'].includes(handoff?.status);
  const activeRequest = automaticApplications.get(token);
  const paymentBlocked = Boolean(
    (checkoutStep && loaded && token && purchase.token !== token) ||
      (ready && settledToken !== token && (unresolved || activeRequest || inFlightToken === token)),
  );

  useEffect(() => {
    if (!ready || settledToken === token) return undefined;
    let mounted = true;
    let retryTimer;
    const retryRead = () => {
      if (mounted) retryTimer = setTimeout(() => setRetryCount((count) => count + 1), 3000);
    };
    const cleanup = () => {
      mounted = false;
      clearTimeout(retryTimer);
    };

    const existingRequest = automaticApplications.get(token);
    if (existingRequest) {
      existingRequest.then(() => {
        if (mounted) setSettledToken(token);
      }, retryRead);
      return cleanup;
    }

    const status = readReferralCouponHandoff(token)?.status;
    if (status === 'settled' || status === 'skipped') {
      setSettledToken(token);
      return cleanup;
    }
    if (status !== 'pending' && status !== 'attempted') return cleanup;

    let code;
    if (status === 'pending') {
      const hasDynamicPricing = getHasDynamicPricing(
        purchase.departs?.fragments?.[0]?.pricing?.discountType,
        purchase.returns?.fragments?.[0]?.pricing?.discountType,
      );
      const fieldAvailable = isCheckoutDiscountCodeAvailable({
        features,
        purchaseStep,
        hasDynamicPricing,
      });

      if (
        !fieldAvailable ||
        purchase.isExchange ||
        purchase.externalCouponCode ||
        manualCode?.trim()
      ) {
        skipReferralCoupon(token);
        setSettledToken(token);
        return cleanup;
      }

      code = claimReferralCoupon(token);
      if (!code) return cleanup;
    }

    setInFlightToken(token);
    const selectedPlan = purchase.selectedInstallmentsPlan || {};
    const apply = () =>
      code ? dispatch(applyDiscount(token, code, selectedPlan, { guardToken: true })) : undefined;
    const reconcile = () => dispatch(reconcileReferralDiscount(token, selectedPlan));
    const request = Promise.resolve()
      .then(apply)
      .then(reconcile, reconcile)
      .then(() => settleReferralCoupon(token));
    automaticApplications.set(token, request);
    request.then(
      () => {
        automaticApplications.delete(token);
        if (mounted) setSettledToken(token);
      },
      () => {
        automaticApplications.delete(token);
        retryRead();
      },
    );
    return cleanup;
  }, [
    ready,
    token,
    settledToken,
    purchaseStep,
    purchase,
    features,
    manualCode,
    dispatch,
    retryCount,
  ]);

  return paymentBlocked;
}
